import { defineComponent, h } from 'vue';
import BaseComponent from './baseFormMixin.jsx';
import variableBindingMixin from './variableBindingMixin.jsx';
import {
    renderVariableAssistIcon,
    renderVariablePill,
} from './variableBindingUi.jsx';
import utils from '../../../Shared/utils.jsx';
import methods from '../../../Shared/methods';
import { QInput, QCard, QCardSection } from 'quasar';

import { appSettings } from '@/Shared/appSettings.js';

import TranslationContainer from '../TranslationContainer.jsx';
import VerticalStack from '../VerticalStack.jsx';

/**
 * Textarea + header Controls (e.g. PlayTTS).
 * Variable assist lives in the header toolbar (not QInput append) so the body stays clean.
 */
export default defineComponent({
    name: 'sform-textareawithheaderbar',
    mixins: [BaseComponent, variableBindingMixin],
    components: {
        TranslationContainer,
        VerticalStack,
        QInput,
        QCard,
        QCardSection,
    },
    data: () => ({
    }),
    props: {
    },
    created() {
    },
    mounted() {
        this.loadComplete();
    },
    computed: {
        placeholder() {
            if (this.element && this.element.schema)
                return this.element.schema.placeholder;
            else
                return undefined;
        },
        textvalue: function () {
            if (typeof this.itemvalue === 'object')
                return JSON.stringify(this.itemvalue);
            else
                return this.itemvalue;
        },
        /** Header toolbar: keep affordance discoverable without crowding the textarea. */
        showHeaderVariableAssist() {
            return this.variableAssistAllowed && !this.isVariableBindingMode;
        },
    },
    methods: {
        renderHeaderVariableAssist() {
            if (!this.showHeaderVariableAssist)
                return null;

            // Match Option A: fully hidden until hover/focus/open (not a persistent faded glyph).
            const emphasize = this._vbHovered || this._vbFocused || this._vbPickerOpen;
            if (!emphasize)
                return null;

            return (
                <div
                    class="c-variable-binding-header-assist"
                    style={{
                        display: 'inline-flex',
                        alignItems: 'center',
                        flex: '0 0 auto',
                        padding: '0 4px',
                    }}
                >
                    {renderVariableAssistIcon({
                        visible: true,
                        pickerOpen: this._vbPickerOpen,
                        onOpenPicker: () => this.openVariablePicker(),
                        menu: this.renderVariablePicker(),
                    })}
                </div>
            );
        },
        renderBoundBody() {
            const name = this.vbBoundName;
            if (!name)
                return null;

            return renderVariablePill({
                name,
                label: this.hasInputLabel ? (this.inputLabel || this.labelText) : undefined,
                onClear: () => this.exitVariableBindingMode(),
                onChange: () => this.openVariablePicker(),
                menu: this.renderVariablePicker(),
            });
        },
        renderLiteralTextarea() {
            let slots = {
                message: ({ message }) => {
                    return <TranslationContainer context={this} value={message}></TranslationContainer>
                }
            };
            if (this.hasInputLabel)
                slots.label = () => <TranslationContainer slot="label" context={this} value={this.labelText}></TranslationContainer>;

            let input = (
                <QInput
                    class="caption scrollbar-style-1 sform-input-white"
                    outlined
                    dense
                    labelSlot={this.hasInputLabel}
                    type="textarea"
                    rows={this.element.formatData?.Rows || 2}
                    style={{ width: '100%' }}
                    modelValue={this.textvalue}
                    placeholder={this.Translate(this.placeholder)}
                    rules={this.rules} {...this.compilerErrorFieldProps}
                    onUpdate:modelValue={(value) => this.sync(value)}
                    onFocus={() => { this._vbFocused = true; this.onFocus(); }}
                    onBlur={() => { this._vbFocused = false; this.onBlur(); }}
                    hint={this.inputHint}
                    hide-hint
                    bottom-slots={this.showFieldBottomSlots}
                    persistent-hint={appSettings.DebugTranslationPrefixSetting}
                >{slots}</QInput>
            );

            if (this.appearance)
                utils.applyVue2ToVue3Props(input, this.appearance);

            return input;
        },
    },
    render() {
        const headerAssist = this.renderHeaderVariableAssist();
        const body = this.isVariableBindingMode
            ? this.renderBoundBody()
            : this.renderLiteralTextarea();

        return (
            <QCard
                flat
                bordered
                class="q-mb-sm q-pb-none c-TextAreaWithHeaderBar"
                onMouseenter={this.onVariableAssistMouseEnter}
                onMouseleave={this.onVariableAssistMouseLeave}
            >
                <QCardSection class="q-pb-none q-pt-none q-pl-xs q-pr-xs">
                    <div
                        class="c-TextAreaWithHeaderBar-toolbar"
                        style={{
                            display: 'flex',
                            alignItems: 'center',
                            gap: '4px',
                            minHeight: '32px',
                            width: '100%',
                        }}
                    >
                        <div style={{ flex: '1 1 auto', minWidth: 0 }}>
                            <VerticalStack
                                type="HorizontalLayout"
                                name={this.name}
                                root={this.root}
                                parentType="InputWithHeaderBar"
                                controlData={this.element.formatData}
                                controlName={this.Name}
                                scopeitems={this.scopeitems}
                                controlscope={this.controlscope}
                            >
                            </VerticalStack>
                        </div>
                        {headerAssist}
                    </div>
                </QCardSection>
                <QCardSection class="q-pb-none">
                    {body}
                </QCardSection>
            </QCard>
        );
    }
});
